'use client';

import type { ts } from '@funkey/shared';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { CheckIcon, ChevronRightIcon, CircleIcon, Download, Loader2 } from 'lucide-react';
import * as React from 'react';

import { cn } from '~/utils';
import { FileDownloadSDK } from '~/utils/files';
import { useAsyncFn } from '~/utils/react';
import useNotifications from '~/features/notifications/useNotifications';
import { useI18n } from '~/features/i18n';

const DropdownMenuCloseContext = React.createContext<() => void>(() => undefined);

/** Programmatically close the nearest enclosing `DropdownMenu`. */
export const useCloseDropdownMenu = () => React.useContext(DropdownMenuCloseContext);

export const DropdownMenu = ({
    open: controlledOpen,
    onOpenChange,
    defaultOpen,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) => {
    const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? false);
    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : internalOpen;
    const handleOpenChange = React.useCallback(
        (next: boolean) => {
            if (!isControlled) setInternalOpen(next);
            onOpenChange?.(next);
        },
        [isControlled, onOpenChange],
    );
    const close = React.useCallback(() => handleOpenChange(false), [handleOpenChange]);
    return (
        <DropdownMenuCloseContext.Provider value={close}>
            <DropdownMenuPrimitive.Root
                data-slot="dropdown-menu"
                open={open}
                onOpenChange={handleOpenChange}
                {...props}
            />
        </DropdownMenuCloseContext.Provider>
    );
};

export const DropdownMenuPortal = ({
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Portal>) => {
    return <DropdownMenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />;
};

export const DropdownMenuTrigger = ({
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>) => {
    return <DropdownMenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />;
};

export const DropdownMenuContent = ({
    className,
    sideOffset = 4,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) => {
    return (
        <DropdownMenuPrimitive.Portal>
            <DropdownMenuPrimitive.Content
                data-slot="dropdown-menu-content"
                sideOffset={sideOffset}
                className={cn(
                    'max-h-(--radix-dropdown-menu-content-available-height) origin-(--radix-dropdown-menu-content-transform-origin) z-50 min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                    className,
                )}
                {...props}
            />
        </DropdownMenuPrimitive.Portal>
    );
};

export const DropdownMenuGroup = ({
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Group>) => {
    return <DropdownMenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />;
};

export const DropdownMenuItem = ({
    className,
    inset,
    variant = 'default',
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean;
    variant?: 'default' | 'destructive';
}) => {
    return (
        <DropdownMenuPrimitive.Item
            data-slot="dropdown-menu-item"
            data-inset={inset}
            data-variant={variant}
            className={cn(
                "data-[variant=destructive]:*:[svg]:!text-destructive outline-hidden relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[inset]:pl-8 data-[variant=destructive]:text-destructive data-[disabled]:opacity-50 data-[variant=destructive]:focus:bg-destructive/10 data-[variant=destructive]:focus:text-destructive dark:data-[variant=destructive]:focus:bg-destructive/20 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0",
                className,
            )}
            {...props}
        />
    );
};

export const DropdownMenuCheckboxItem = ({
    className,
    children,
    checked,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) => {
    return (
        <DropdownMenuPrimitive.CheckboxItem
            data-slot="dropdown-menu-checkbox-item"
            className={cn(
                "outline-hidden relative flex cursor-pointer select-none items-center gap-2 rounded-sm py-1.5 pl-8 pr-2 text-sm focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
                className,
            )}
            checked={checked}
            {...props}
        >
            <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
                <DropdownMenuPrimitive.ItemIndicator>
                    <CheckIcon className="size-4" />
                </DropdownMenuPrimitive.ItemIndicator>
            </span>
            {children}
        </DropdownMenuPrimitive.CheckboxItem>
    );
};

export const DropdownMenuRadioGroup = ({
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioGroup>) => {
    return <DropdownMenuPrimitive.RadioGroup data-slot="dropdown-menu-radio-group" {...props} />;
};

export const DropdownMenuRadioItem = ({
    className,
    children,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) => {
    return (
        <DropdownMenuPrimitive.RadioItem
            data-slot="dropdown-menu-radio-item"
            className={cn(
                "outline-hidden relative flex cursor-pointer select-none items-center gap-2 rounded-sm py-1.5 pl-8 pr-2 text-sm focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
                className,
            )}
            {...props}
        >
            <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
                <DropdownMenuPrimitive.ItemIndicator>
                    <CircleIcon className="size-2 fill-current" />
                </DropdownMenuPrimitive.ItemIndicator>
            </span>
            {children}
        </DropdownMenuPrimitive.RadioItem>
    );
};

export const DropdownMenuLabel = ({
    className,
    inset,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
}) => {
    return (
        <DropdownMenuPrimitive.Label
            data-slot="dropdown-menu-label"
            data-inset={inset}
            className={cn('px-2 py-1.5 text-sm font-medium data-[inset]:pl-8', className)}
            {...props}
        />
    );
};

export const DropdownMenuSeparator = ({
    className,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) => {
    return (
        <DropdownMenuPrimitive.Separator
            data-slot="dropdown-menu-separator"
            className={cn('-mx-1 my-1 h-px bg-border', className)}
            {...props}
        />
    );
};

export const DropdownMenuShortcut = ({ className, ...props }: React.ComponentProps<'span'>) => {
    return (
        <span
            data-slot="dropdown-menu-shortcut"
            className={cn('ml-auto text-xs tracking-widest text-muted-foreground', className)}
            {...props}
        />
    );
};

export const DropdownMenuSub = ({
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Sub>) => {
    return <DropdownMenuPrimitive.Sub data-slot="dropdown-menu-sub" {...props} />;
};

export const DropdownMenuSubTrigger = ({
    className,
    inset,
    children,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
}) => {
    return (
        <DropdownMenuPrimitive.SubTrigger
            data-slot="dropdown-menu-sub-trigger"
            data-inset={inset}
            className={cn(
                'outline-hidden flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[inset]:pl-8 data-[state=open]:text-accent-foreground',
                className,
            )}
            {...props}
        >
            {children}
            <ChevronRightIcon className="ml-auto size-4" />
        </DropdownMenuPrimitive.SubTrigger>
    );
};

export const DropdownMenuSubContent = ({
    className,
    ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubContent>) => {
    return (
        <DropdownMenuPrimitive.SubContent
            data-slot="dropdown-menu-sub-content"
            className={cn(
                'origin-(--radix-dropdown-menu-content-transform-origin) z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                className,
            )}
            {...props}
        />
    );
};

/**
 * `DropdownMenuItem` that fetches a file in the background, downloads it, and
 * closes the enclosing `DropdownMenu` on success. Shows a spinner while
 * loading, blocks repeat clicks, and dispatches an error notification on
 * failure.
 */
export const DropdownMenuItem_DownloadButton = ({
    url,
    filename,
    className,
    children,
    ...props
}: Omit<
    React.ComponentProps<typeof DropdownMenuPrimitive.Item>,
    'onSelect' | 'asChild' | 'disabled'
> & {
    url: string;
    filename: string;
}) => {
    const i18n = useI18n();
    const notifications = useNotifications();
    const closeMenu = useCloseDropdownMenu();
    const [loading, setLoading] = React.useState(false);

    return (
        <DropdownMenuPrimitive.Item
            data-slot="dropdown-menu-item"
            data-loading={loading || undefined}
            aria-disabled={loading || undefined}
            className={cn(
                "outline-hidden relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm focus:bg-accent focus:text-accent-foreground aria-disabled:cursor-wait aria-disabled:opacity-70 data-[disabled]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0",
                className,
            )}
            onSelect={async event => {
                event.preventDefault();
                if (loading) return;
                setLoading(true);
                try {
                    await FileDownloadSDK.downloadURL(url, filename);
                    closeMenu();
                } catch {
                    notifications.dispatchError({
                        message: i18n._('Export failed'),
                    });
                } finally {
                    setLoading(false);
                }
            }}
            {...props}
        >
            {loading ? <Loader2 className="animate-spin" /> : <Download />}
            {children}
        </DropdownMenuPrimitive.Item>
    );
};

export const DropdownMenuItem_AsyncAction: React.FC<
    ts.Object.override<
        React.ComponentProps<typeof DropdownMenuItem>,
        {
            onSelect(event: Event): Promise<unknown>;
            onError?(error: unknown): void | Promise<unknown>;
            children:
                | React.ReactNode
                | React.FC<{
                      isPending: boolean;
                  }>;
        }
    >
> = ({ onError, ...props }) => {
    const onSelect = useAsyncFn<typeof props.onSelect>(async e => {
        e.preventDefault();
        try {
            return await props.onSelect(e);
        } catch (error) {
            await onError?.(error);
        }
    });

    return (
        <DropdownMenuItem {...props} onSelect={onSelect}>
            {typeof props.children === 'function' ? (
                <props.children isPending={onSelect.isPending} />
            ) : (
                props.children
            )}
        </DropdownMenuItem>
    );
};
